import { useAppDispatch } from '@/app/app.store';
import {
  useOtpVerifyMutation,
  useSendOtpMutation,
} from '@/app/features/auth/auth.api';
import { setPlanInfo } from '@/app/features/onboarding/onboarding.slice';
import { Button, Input } from 'antd';
import { useEffect, useState, type JSX } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { toast } from '@/lib/toast';
import { errorToast, formatSecToTime } from '@/utils';

import IMAGES from '@/assets/images';
import AuthLayout from '@/layouts/AuthLayout';

const OTP_LENGTH = 6;
const COUNTDOWN_SECONDS = 120;
const STORAGE_KEY = 'otpExpiry'; // Key for localStorage

export default function VerifyOTP() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [otpVerify, { isLoading }] = useOtpVerifyMutation();
  const [sendOtp, { isLoading: isOtpLoading }] = useSendOtpMutation();

  const [params, setParams] = useSearchParams();
  const email = params.get('email');
  const planId = params.get('planId');
  const planType = params.get('planType');

  // Lazy initialization for countdown from localStorage
  const [countdown, setCountdown] = useState(() => {
    const savedExpiry = localStorage.getItem(STORAGE_KEY);
    if (savedExpiry) {
      const expiry = parseInt(savedExpiry, 10);
      if (expiry > Date.now()) {
        return Math.ceil((expiry - Date.now()) / 1000);
      } else {
        localStorage.removeItem(STORAGE_KEY);
        return 0;
      }
    } else {
      // First time: set expiry immediately
      const newExpiry = Date.now() + COUNTDOWN_SECONDS * 1000;
      localStorage.setItem(STORAGE_KEY, newExpiry.toString());
      return COUNTDOWN_SECONDS;
    }
  });

  // Lazy initialization for isResendAble from localStorage
  const [isResendAble, setIsResendAble] = useState(() => {
    const savedExpiry = localStorage.getItem(STORAGE_KEY);
    if (savedExpiry) {
      const expiry = parseInt(savedExpiry, 10);
      return expiry <= Date.now();
    }
    return false;
  });

  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(''));

  // Check if OTP is fully entered (all 6 digits)
  const isOtpComplete = otp.join('').length === OTP_LENGTH;

  // Countdown effect
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;
    if (countdown > 0 && !isResendAble) {
      interval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            setIsResendAble(true);
            localStorage.removeItem(STORAGE_KEY); // Clear on expiry
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [countdown, isResendAble]);

  const handleChange = (index: number, value: string) => {
    if (/^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value.slice(-1);
      setOtp(newOtp);
      if (value && index < OTP_LENGTH - 1) {
        (
          document.getElementById(`otp-${index + 1}`) as HTMLInputElement
        )?.focus();
      }
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const newOtp = [...otp];

      if (otp[index]) {
        // If current field has value, clear it
        newOtp[index] = '';
        setOtp(newOtp);
      } else if (index > 0) {
        // If current field is empty, move to previous field and clear it
        newOtp[index - 1] = '';
        setOtp(newOtp);
        (
          document.getElementById(`otp-${index - 1}`) as HTMLInputElement
        )?.focus();
      }
    } else if (e.key === 'Enter') {
      // Submit OTP when Enter is pressed and OTP is complete
      if (isOtpComplete) {
        handleConfirm();
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain');
    const digits = pastedData.replace(/\D/g, '').slice(0, OTP_LENGTH);

    if (digits) {
      const newOtp = [...otp];
      for (let i = 0; i < digits.length && i < OTP_LENGTH; i++) {
        newOtp[i] = digits[i];
      }
      setOtp(newOtp);

      // Focus on the next empty field or the last field
      const nextIndex = Math.min(digits.length, OTP_LENGTH - 1);
      (
        document.getElementById(`otp-${nextIndex}`) as HTMLInputElement
      )?.focus();
    }
  };

  const handleConfirm = async () => {
    if (isOtpComplete) {
      const values = {
        email,
        code: otp.join(''),
      };

      try {
        await otpVerify(values).unwrap();
        toast('success', 'Login successfully');
        navigate('/');

        // Clear storage and reset for next time
        localStorage.removeItem(STORAGE_KEY);
        setCountdown(0);
        setIsResendAble(true);
      } catch (err) {
        errorToast(err, 'Login fail, please try again');
      }
    }
  };

  const handleResend = async () => {
    try {
      await sendOtp({ email }).unwrap();
      setOtp(Array(OTP_LENGTH).fill(''));
      toast('success', 'OTP send successfully');

      // Set new expiry timestamp
      const newExpiry = Date.now() + COUNTDOWN_SECONDS * 1000;
      localStorage.setItem(STORAGE_KEY, newExpiry.toString());

      setCountdown(COUNTDOWN_SECONDS);
      setIsResendAble(false);

      // Focus on first input after resend
      (document.getElementById('otp-0') as HTMLInputElement)?.focus();
    } catch (err) {
      errorToast(err, 'OTP send fail, please try again');
    }
  };

  useEffect(() => {
    if (planId && planType) {
      dispatch(
        setPlanInfo({
          id: planId,
          billingType: planType as 'monthly' | 'yearly',
        }),
      );
      params.delete('planId');
      params.delete('planType');
      setParams(params);
    }
  }, [planId, planType, dispatch, params, setParams]);

  return (
    <AuthLayout
      leftContent={
        <div className="relative z-10 flex flex-col justify-center items-center mt-32">
          <div className="text-white text-2xl font-normal">Welcome to</div>

          <div className="flex items-center justify-center mt-4 w-56 h-32">
            <img
              src={IMAGES.mainLogo}
              alt="main_logo"
              className="w-56 h-32 object-contain"
            />
          </div>
        </div>
      }
    >
      <div className="w-full h-full flex flex-col justify-center items-center">
        <div className="bg-transparent p-10 rounded-lg flex flex-col items-center gap-4">
          <div className="flex flex-col justify-center items-center mb-8 md:hidden">
            <div className="text-white text-2xl font-normal">Welcome to</div>

            <div className="flex items-center justify-center mt-4 w-56 h-32">
              <img
                src={IMAGES.mainLogo}
                alt="main_logo"
                className="w-56 h-32 object-contain"
              />
            </div>
          </div>
          <div className="text-white text-lg mb-4 font-semibold">OTP</div>
          <div className="flex gap-2">
            {otp.reduce<JSX.Element[]>((acc, value, index) => {
              acc.push(
                <Input
                  key={index}
                  id={`otp-${index}`}
                  value={value}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  maxLength={1}
                  className="w-12 text-center"
                  autoComplete="off"
                />,
              );
              return acc;
            }, [])}
          </div>
          <div className="flex justify-between w-full text-sm text-gray-400 mt-2">
            <button
              className={`cursor-pointer text-theme-primary underline disabled:text-gray-500 disabled:cursor-not-allowed`}
              onClick={() => !isOtpLoading && handleResend()}
              disabled={!isResendAble || isOtpLoading}
            >
              {isOtpLoading ? 'Resending...' : 'Resend'}
            </button>

            <span>{isResendAble ? '' : formatSecToTime(countdown)}</span>
          </div>
          <Button
            type="primary"
            className="w-full h-10 bg-teal-500 !text-neutral-700 hover:!text-neutral-700 font-semibold mt-4"
            onClick={handleConfirm}
            disabled={!isOtpComplete}
            loading={isLoading}
          >
            Confirm
          </Button>
        </div>
      </div>
    </AuthLayout>
  );
}
